import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GuidedChatbotState,
  GuidedChat,
  GuidedChatMessage,
  GuidedChatsListData,
  GuidedMessagesListData,
  ActiveDecisionTree,
  BotResponse,
} from '../../types/guidedChatbot.types';

const initialState: GuidedChatbotState = {
  chats: [],
  selectedChatId: null,
  messages: [],
  loading: false,
  loadingMessages: false,
  loadingOlderMessages: false,
  sendingMessage: false,
  error: null,
  chatsPagination: {
    page: 1,
    pageSize: 20,
    totalRecords: 0,
    hasMorePages: false,
  },
  messagesPagination: {
    page: 1,
    pageSize: 50,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
  // Guided-specific
  treeKey: null,
  treeName: null,
  trees: [],
  loadingTrees: false,
  currentBotResponse: null,
  inputEnabled: false,
};

const guidedChatbotSlice = createSlice({
  name: 'guidedChatbot',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingMessages: (state, action: PayloadAction<boolean>) => {
      state.loadingMessages = action.payload;
    },
    setLoadingOlderMessages: (state, action: PayloadAction<boolean>) => {
      state.loadingOlderMessages = action.payload;
    },
    setSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.sendingMessage = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChats: (state, action: PayloadAction<GuidedChatsListData>) => {
      state.chats = action.payload.chats;
      state.chatsPagination = {
        ...state.chatsPagination,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
    },
    appendChats: (state, action: PayloadAction<GuidedChatsListData>) => {
      state.chats = [...state.chats, ...action.payload.chats];
      state.chatsPagination = {
        ...state.chatsPagination,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
    },
    setSelectedChatId: (state, action: PayloadAction<string | null>) => {
      state.selectedChatId = action.payload;
    },
    setMessages: (state, action: PayloadAction<GuidedMessagesListData>) => {
      const sorted = [...action.payload.messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      state.messages = sorted;
      state.messagesPagination = {
        ...state.messagesPagination,
        page: 1,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loadingMessages = false;
    },
    prependMessages: (state, action: PayloadAction<GuidedMessagesListData>) => {
      const sorted = [...action.payload.messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      state.messages = [...sorted, ...state.messages];
      state.messagesPagination = {
        ...state.messagesPagination,
        page: state.messagesPagination.page + 1,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loadingOlderMessages = false;
    },
    addMessage: (state, action: PayloadAction<GuidedChatMessage>) => {
      state.messages.push(action.payload);
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.chatsPagination.page = 1;
    },
    setChatPage: (state, action: PayloadAction<number>) => {
      state.chatsPagination.page = action.payload;
    },
    addChatToTop: (state, action: PayloadAction<GuidedChat>) => {
      const existing = state.chats.findIndex((c) => c.id === action.payload.id);
      if (existing !== -1) {
        state.chats.splice(existing, 1);
      }
      state.chats.unshift(action.payload);
    },
    deleteChat: (state, action: PayloadAction<string>) => {
      state.chats = state.chats.filter((c) => c.id !== action.payload);
      state.chatsPagination.totalRecords -= 1;
      if (state.selectedChatId === action.payload) {
        state.selectedChatId = null;
        state.messages = [];
        state.messagesPagination = { page: 1, pageSize: 50, totalRecords: 0, hasMorePages: false };
        state.treeKey = null;
        state.treeName = null;
        state.currentBotResponse = null;
        state.inputEnabled = false;
      }
    },
    clearMessages: (state) => {
      state.messages = [];
      state.selectedChatId = null;
      state.messagesPagination = { page: 1, pageSize: 50, totalRecords: 0, hasMorePages: false };
      state.treeKey = null;
      state.treeName = null;
      state.currentBotResponse = null;
      state.inputEnabled = false;
    },
    // Guided-specific
    setTrees: (state, action: PayloadAction<ActiveDecisionTree[]>) => {
      state.trees = action.payload;
    },
    setLoadingTrees: (state, action: PayloadAction<boolean>) => {
      state.loadingTrees = action.payload;
    },
    setTreeKey: (state, action: PayloadAction<string | null>) => {
      state.treeKey = action.payload;
    },
    setTreeName: (state, action: PayloadAction<string | null>) => {
      state.treeName = action.payload;
    },
    setCurrentBotResponse: (state, action: PayloadAction<BotResponse | null>) => {
      state.currentBotResponse = action.payload;
    },
    setInputEnabled: (state, action: PayloadAction<boolean>) => {
      state.inputEnabled = action.payload;
    },
    resetGuidedChatbot: () => initialState,
  },
});

export const {
  setLoading,
  setLoadingMessages,
  setLoadingOlderMessages,
  setSendingMessage,
  setError,
  setChats,
  appendChats,
  setSelectedChatId,
  setMessages,
  prependMessages,
  addMessage,
  setSearch,
  setChatPage,
  addChatToTop,
  deleteChat,
  clearMessages,
  setTrees,
  setLoadingTrees,
  setTreeKey,
  setTreeName,
  setCurrentBotResponse,
  setInputEnabled,
  resetGuidedChatbot,
} = guidedChatbotSlice.actions;

export default guidedChatbotSlice.reducer;
